import React, { useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import WaitlistSourceDialog from '@/components/WaitlistSourceDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://api.pluckr.ai');

export const CTASection = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success

  const [isSourceDialogOpen, setIsSourceDialogOpen] = useState(false);
  const [sourceStatus, setSourceStatus] = useState('idle'); // idle | loading | success | already
  const [selectedSource, setSelectedSource] = useState(null);
  const [pendingEmail, setPendingEmail] = useState('');
  const [resultMessage, setResultMessage] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setPendingEmail(email);
    setSelectedSource(null);
    setSourceStatus('idle');
    setResultMessage('');
    setIsSourceDialogOpen(true);
  };

  const submitWaitlist = async (source) => {
    // Optimistic UX: show thanks immediately, do backend work in parallel.
    setSelectedSource(source);
    setSourceStatus('success');
    setStatus('success');
    setResultMessage("You're on the waitlist. We'll inform you soon.");

    try {

      const response = await fetch(`${API_URL}/api/waitlist/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: pendingEmail,
          source,
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload?.success === false) {
        throw new Error(payload?.message || 'Failed to join waitlist');
      }

      if (payload?.alreadyJoined) {
        setSourceStatus('already');
        setResultMessage(
          payload?.message ||
          "We get it — you can't wait. We'll get back to you real quick."
        );
      } else {
        setSourceStatus('success');
        setResultMessage(
          payload?.message || "You're on the waitlist. We'll inform you soon."
        );
      }

      toast.success(payload?.message || "You're on the list! We'll notify you when we launch.");
      setEmail('');
      setPendingEmail('');
    } catch (err) {
      // If backend fails, revert optimistic success.
      setStatus('idle');
      setSourceStatus('idle');
      setResultMessage('');
      toast.error(err?.message || 'Something went wrong');
    }
  };

  return (
    <section
      className="relative py-16 md:py-32 lg:py-40 overflow-hidden bg-[linear-gradient(180deg,#ffffff_0%,#f8f9fa_50%,#e8f5e2_100%)] dark:bg-[linear-gradient(180deg,#161616_0%,#161616_55%,rgba(155,227,143,0.08)_100%)]"
      data-testid="cta-section"
    >
      {/* Background floating plugins */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="absolute w-20 h-20 rounded-xl opacity-10"
            style={{
              left: `${10 + i * 15}%`,
              top: `${20 + (i % 3) * 25}%`,
              background: 'linear-gradient(135deg, #9be38f 0%, #2d8a2d 100%)',
              filter: 'blur(40px)',
              transform: `rotate(${i * 15}deg)`,
              animation: `float ${8 + i}s ease-in-out infinite`,
              animationDelay: `${i * 0.5}s`
            }}
          />
        ))}
      </div>

      <div className="max-w-[900px] mx-auto px-6 lg:px-20 relative z-10">
        <WaitlistSourceDialog
          open={isSourceDialogOpen}
          onOpenChange={(open) => {
            // Don’t allow closing while the submission is in-flight.
            setIsSourceDialogOpen(open);
          }}
          onSelect={setSelectedSource}
          onSubmit={submitWaitlist}
          status={sourceStatus}
          selected={selectedSource}
          resultMessage={resultMessage}
        />

        {/* Headline */}
        <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-center mb-4 md:mb-6 leading-[1.2] md:leading-tight">
          <span className="text-[#1a1a1a] dark:text-white">Stop Debugging.</span>
          <br />
          <span
            className="bg-clip-text text-transparent bg-gradient-to-r from-[#1a1a1a] to-[#2d8a2d] dark:from-white dark:to-[#9be38f]"
          >
            Start Shipping.
          </span>
        </h2>

        {/* Subheadline */}
        <p className="text-base md:text-xl lg:text-2xl text-[#5a5a5a] dark:text-[#e0e0e0] text-center max-w-[700px] mx-auto mb-8 md:mb-12 px-2">
          Join the waitlist for early access to the plugin registry
          that makes AI builders actually useful.
        </p>

        {/* Email Form */}
        <form onSubmit={handleSubmit} className="max-w-[600px] mx-auto">
          <div className="cta-form-container flex flex-col gap-3 md:gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="w-full bg-white dark:bg-[#282929] border border-gray-200 dark:border-white/15 rounded-xl text-[#1a1a1a] dark:text-white text-base md:text-lg px-5 md:px-6 py-4 outline-none placeholder:text-[#999999] dark:placeholder:text-white/40 focus:border-[#9be38f] focus:ring-2 focus:ring-[#9be38f]/20 transition-all"
              disabled={status === 'loading' || status === 'success'}
              data-testid="email-input"
            />
            <button
              type="submit"
              disabled={status === 'loading' || status === 'success'}
              className={`w-full px-6 md:px-8 py-4 rounded-xl font-semibold text-base md:text-lg transition-all whitespace-nowrap ${status === 'success'
                ? 'bg-[#9be38f] text-[#1a1a1a]'
                : 'bg-[#9be38f] text-[#1a1a1a] hover:bg-[#8ad37f]'
                }`}
              style={{
                boxShadow: status === 'success'
                  ? '0 0 30px rgba(155, 227, 143, 0.4)'
                  : '0 0 20px rgba(155, 227, 143, 0.3)'
              }}
              data-testid="submit-btn"
            >
              {status === 'loading' && (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Joining...
                </span>
              )}
              {status === 'success' && (
                <span className="flex items-center gap-2">
                  <Check className="w-5 h-5" />
                  You're on the list!
                </span>
              )}
              {status === 'idle' && 'Join the waitlist'}
            </button>
          </div>
        </form>

      </div>
    </section>
  );
};

export default CTASection;
